import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';
import { atom, useRecoilState } from 'recoil';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    Description,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    EmptyState,
    EmptyStateIcon,
    Link,
    LoadingPanel,
    TextInput,
} from '@gitbook/spine-react';
import { useDelayedValue, useEventCallback } from '@gitbook/util-react-hooks';

import { apiClient, apiQuery } from '../../state';
import { ImageGridItem, ImagesGrid } from '../Files';

const unplashQueryAtom = atom({
    key: 'unsplashQuery',
    default: '',
});

/**
 * Tab suggesting unsplash photos when picking an images.
 */
export function TabUnsplash(props: {
    onSelect: (urls: api.ContentRefURL[]) => void;
}): React.ReactElement {
    const { onSelect } = props;
    const [query, setQuery] = useRecoilState(unplashQueryAtom);
    const delayedQuery = useDelayedValue(query.trim(), 600);

    return (
        <div className="max-h-[55vh] overflow-y-auto">
            <DialogBody>
                <div className="flex flex-col gap-6">
                    <TextInput
                        leadingIcon={Icon.Search}
                        placeholder="Search for an image…"
                        value={query}
                        autoFocus
                        onChange={(event) => setQuery(event.target.value)}
                    />

                    <React.Suspense fallback={<LoadingPanel size="xlarge" />}>
                        <UnsplashPhotosList query={delayedQuery} onSelect={onSelect} />
                    </React.Suspense>
                </div>
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
            </DialogFooter>
        </div>
    );
}

/** Search and list all images matching a query */
function UnsplashPhotosList(props: {
    query: string;
    onSelect: (ref: api.ContentRefURL[]) => void;
}): React.ReactElement {
    const { query, onSelect } = props;

    const { data: results } = useSuspenseQuery(
        apiQuery('searchUnsplashImages', { query: query || undefined })
    );

    const onSelectImage = useEventCallback(async (image: api.UnsplashImage) => {
        // To be compliant with the API Guidelines, we track the download
        apiClient.internal.trackDownloadUnplashImage({
            downloadLocation: image.downloadLocation,
        });

        onSelect([{ kind: 'url', url: image.urls.full }]);
    });

    if (results.items.length === 0) {
        return (
            <EmptyState>
                <EmptyStateIcon>
                    <Icon.Search />
                </EmptyStateIcon>
                <Description>No images found for the search. Try another query.</Description>
            </EmptyState>
        );
    }

    return (
        <ImagesGrid>
            {results.items.map((item) => (
                <UnplashImageItem
                    item={item}
                    onPress={() => {
                        onSelectImage(item);
                    }}
                />
            ))}
        </ImagesGrid>
    );
}

/** An entry in the list for an image */
function UnplashImageItem(props: {
    item: api.UnsplashImage;
    onPress: () => void;
}): React.ReactElement {
    const { item, onPress } = props;

    return (
        <ImageGridItem src={item.urls.small} onPress={onPress}>
            by{' '}
            <Link kind="inherit" target="_blank" href={item.author.url}>
                {item.author.name}
            </Link>
        </ImageGridItem>
    );
}
